import {
  Href,
  ImperativeRouter,
  useGlobalSearchParams,
  useRouter,
} from "expo-router";
import { useMemo } from "react";

import {
  type PropagatedUrlParams,
  isPropagatedParamKey,
  mergePropagatedUrlParams,
} from "@/utils/propagatedUrlParams";

const usePropagatedUrlParams = (): PropagatedUrlParams => {
  const searchParams =
    useGlobalSearchParams<Record<string, string | string[]>>();

  return useMemo(() => {
    const propagatedUrlParams: PropagatedUrlParams = {};
    for (const [key, value] of Object.entries(searchParams)) {
      if (isPropagatedParamKey(key) && value !== undefined) {
        propagatedUrlParams[key] = value;
      }
    }
    return propagatedUrlParams;
  }, [searchParams]);
};

export const usePropagatedHref = (href: Href) =>
  mergePropagatedUrlParams(href, usePropagatedUrlParams());

type Navigate = ImperativeRouter["push"];
type NavigateMethod = "push" | "replace" | "navigate" | "dismissTo";

export const usePropagatedRouter = (): ImperativeRouter => {
  const router = useRouter();
  const propagatedUrlParams = usePropagatedUrlParams();

  return useMemo(() => {
    const propagate =
      (method: NavigateMethod): Navigate =>
      (href, ...rest) =>
        router[method](
          mergePropagatedUrlParams(href, propagatedUrlParams),
          ...rest,
        );

    return {
      ...router,
      push: propagate("push"),
      replace: propagate("replace"),
      navigate: propagate("navigate"),
      dismissTo: propagate("dismissTo"),
    };
  }, [router, propagatedUrlParams]);
};
